import CT from '@marketplace-ai/tags';
import type {
  ColDef,
  GetRowIdParams,
  GridApi,
  PaginationChangedEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import {
  AllCommunityModule,
  colorSchemeLightCold,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { LoadingSpinner } from '~/components';
import { BASE_GRID_STYLE } from '~/components/Table/gridUtil';
import { withNewRecordPill } from '~/features/vault/newRecords/NewRecordPill';
import { useNewRecordPills } from '~/features/vault/newRecords/useNewRecords';
import { restoreColumnState, saveColumnState, shouldPersistColumnEvent } from './columnState';
import { FilterBanner, useFilterBanner } from './FilterBanner';

ModuleRegistry.registerModules([AllCommunityModule]);
const theme = themeQuartz.withPart(colorSchemeLightCold).withParams(BASE_GRID_STYLE);

export interface GridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid data is any for generic usage
  data: any[];
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid ColDef is generic with any
  colDefs: ColDef<any, any>[];
  pagination: boolean;
  cyTag: string;
  rowSelection?: RowSelectionOptions;
  paginationPageSize?: number;
  paginationPageSizeSelector?: number[];
  enableFilterHandlers?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid event is any
  onRowClicked?: (event: any) => void;
  onPaginationChanged?: (event: PaginationChangedEvent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid GetRowIdParams is generic with any
  getRowId?: (params: GetRowIdParams<any>) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid onGridReady params is any
  onGridReady?: (params: any) => void;
  noRowsOverlayComponent?: React.ComponentType;
  /**
   * [MCV-1812] The vault table this grid lists (e.g. `medical_licenses`). Rows a document parse
   * created show a "New" pill in the primary column for this visit, and are marked seen once
   * they have been rendered here.
   */
  newRecordsTable?: string;
}

export interface BasicGridRef {
  refreshCells: () => void;
  redrawRows: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid row data is any for generic usage
  updateRowData: (rowId: string, newData: any) => void;
}

export const BasicGrid = forwardRef<BasicGridRef, GridProps>(
  (
    {
      data,
      loading,
      colDefs,
      pagination,
      rowSelection,
      // MCV-1288: intentionally no default here. When a caller omits paginationPageSize
      // we turn on paginationAutoPageSize below so ag-grid sizes each page to the
      // available container height. Callers that pass an explicit page size keep the
      // old fixed-size behavior and get the page-size-selector.
      paginationPageSize,
      paginationPageSizeSelector = [10, 20, 50],
      cyTag,
      enableFilterHandlers = true,
      onRowClicked,
      onPaginationChanged,
      getRowId,
      onGridReady,
      noRowsOverlayComponent,
      newRecordsTable,
    },
    ref
  ) => {
    const newRecordIds = useNewRecordPills(newRecordsTable, data);
    const columnDefs = useMemo(
      () => withNewRecordPill(colDefs, newRecordIds),
      [colDefs, newRecordIds]
    );

    const defaultColDef = useMemo<ColDef>(() => {
      return {
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          paddingTop: '8px',
          paddingBottom: '8px',
          lineHeight: '1.5',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        filter: true,
        sortable: true,
        resizable: true,
        flex: 1,
        // Truncate with ellipsis instead of wrapping so rows stay on one line
        // and the grid fits more data per viewport. Any custom cell renderer
        // can still opt back in by setting wrapText/autoHeight on its colDef.
        wrapText: false,
        autoHeight: false,
        // Surface the full value in a tooltip on hover so truncated cells
        // remain discoverable. `valueFormatted` is preferred when a column
        // ran its value through a formatter (e.g. date columns).
        tooltipValueGetter: (params) => {
          const formatted = params.valueFormatted;
          if (formatted != null && formatted !== '') return String(formatted);
          const raw = params.value;
          if (raw == null) return '';
          if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
          return '';
        },
      };
    }, []);

    const gridRef = useRef<AgGridReact>(null);
    const { filtersActive, handleFilterChanged, clearFilters, registerGridApi } = useFilterBanner();

    // [MCV-1405] Persist only finished, user-initiated changes — ag-Grid raises
    // these same events mid-drag and for programmatic causes (a new `columnDefs`
    // array, flex, data refreshes), which must not overwrite the saved
    // arrangement. See `shouldPersistColumnEvent` for the guard and its tests.
    const persistColumnState = useCallback(
      (event: { api: GridApi; finished?: boolean; source?: string }) => {
        if (!shouldPersistColumnEvent(event)) return;
        saveColumnState(cyTag, event.api);
      },
      [cyTag]
    );

    // Expose grid API methods
    useImperativeHandle(ref, () => ({
      refreshCells: () => {
        if (gridRef.current?.api) {
          gridRef.current.api.refreshCells();
        }
      },
      redrawRows: () => {
        if (gridRef.current?.api) {
          gridRef.current.api.redrawRows();
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ag-grid row data is any for generic usage
      updateRowData: (rowId: string, newData: any) => {
        if (gridRef.current?.api) {
          const rowNode = gridRef.current.api.getRowNode(rowId);
          if (rowNode) {
            rowNode.updateData(newData);
          }
        }
      },
    }));

    // MCV-1288: auto-page when the caller didn't specify an explicit size. We still
    // pass paginationPageSize along so ag-grid uses it as the initial value when
    // auto is off (legacy callers keep their behavior).
    const autoPageSize = pagination && paginationPageSize === undefined;

    return (
      <div
        style={{
          // MCV-1288: shrink with viewport so the paginator always stays reachable
          // without scrolling. Uses `height` (not `maxHeight`) so ag-Grid's
          // ResizeObserver sees a definite pixel value — required for
          // paginationAutoPageSize to compute the page size correctly. No
          // pixel floor: a fixed minHeight would reintroduce the overflow at
          // viewports < 600px (the exact range this change is meant to help).
          // ag-Grid handles very small containers on its own — autoPageSize
          // will pick 0–1 rows rather than overflow.
          //
          // The 360px chrome budget covers: page header + py-6 padding (~110px)
          // PLUS the sticky `ButtonFooter` (`sticky bottom-0 z-0`, ~80px) used
          // on detail pages. Without that reservation the paginator + last-row
          // action buttons render under the footer on short viewports.
          height: 'min(520px, calc(100vh - 360px))',
        }}
        className="flex flex-col overflow-hidden rounded-lg border border-grey-900"
      >
        <FilterBanner active={filtersActive} onClear={clearFilters} />
        <div className="h-full w-full flex-1" data-cy={cyTag}>
          <AgGridReact
            theme={theme}
            // [MCV-1405] ag-Grid documents this as defaulting to false, meaning
            // a new `columnDefs` array resets displayed columns to definition
            // order — and several callers build theirs inline, so a new array
            // arrives on every parent render. Set defensively: the reproduction
            // below persists rather than resets on ag-grid 34.3.1, but relying
            // on that undocumented leniency to protect a restored order would
            // be a poor bet across upgrades.
            maintainColumnOrder
            // [MCV-1405] Dragging a column off the grid entirely hides it by
            // default, and nothing persists `hide` — so a column dragged off
            // and back on reload reappears, which reads as a confusing,
            // unrelated bug. Drop it back into place instead.
            suppressDragLeaveHidesColumns
            colResizeDefault="shift"
            tooltipShowDelay={350}
            enableFilterHandlers={enableFilterHandlers}
            rowData={data}
            loading={loading}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection={rowSelection}
            pagination={pagination}
            paginationAutoPageSize={autoPageSize}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={autoPageSize ? false : paginationPageSizeSelector}
            getRowId={getRowId ?? ((params) => String(params.data.id))}
            loadingOverlayComponent={() => (
              <LoadingSpinner cyTag={CT.components.loadingSpinner.spinner} />
            )}
            noRowsOverlayComponent={noRowsOverlayComponent}
            onRowClicked={onRowClicked}
            onPaginationChanged={onPaginationChanged}
            onFilterChanged={handleFilterChanged}
            // [MCV-1405] Columns are movable and sortable, but neither survived
            // leaving the page. Persist on change and replay once ready.
            onColumnMoved={persistColumnState}
            onSortChanged={persistColumnState}
            onGridReady={(params) => {
              restoreColumnState(cyTag, params.api);
              registerGridApi(params.api);
              if (onGridReady) {
                onGridReady(params);
              }
            }}
            ref={gridRef}
          />
        </div>
      </div>
    );
  }
);

BasicGrid.displayName = 'BasicGrid';
